import React, { useState, useEffect, useMemo, useContext, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import leagueService from '../services/leagueService';
import predictionService from '../services/predictionService';
import matchService from '../services/matchService';
import { preloadBadges } from '../utils/badgePreloader';
import { useParams, Link, useNavigate } from 'react-router-dom';
import '../styles/LeagueDetails.css';
import '../styles/MatchCard.css';
import MatchCard from './MatchCard';
import MatchDaysStrip from './MatchDaysStrip';
import RankingsTable from './RankingsTable';
import LeagueHeader from './LeagueHeader';
import { AuthContext } from './AuthContext';
import { toast } from 'sonner';
import { trackEvent } from '../utils/analytics';
import { FaRobot, FaTimes, FaChartLine, FaChevronDown, FaChevronUp, FaCoins } from 'react-icons/fa';
import SEO from './SEO';
import PredictionModal from './PredictionModal';
import MatchOddsModal from './MatchOddsModal';
import { translateRound, isNumericRound, translateLeague } from '../utils/roundTranslator';

import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Typography,
	IconButton,
	useMediaQuery,
	Box
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

const LeagueDetail = () => {
	const { isLoggedIn, role, userName } = useContext(AuthContext);
	const { leagueId } = useParams();
	const { t } = useTranslation();
	const navigate = useNavigate();
	const now = new Date();

	const isMobile = useMediaQuery('(max-width:768px)');

	const [league, setLeague] = useState(null);
	const [predictions, setPredictions] = useState({});
	const [error, setError] = useState('');
	const [rankings, setRankings] = useState([]);
	const [selectedDate, setSelectedDate] = useState(null);
	const [currentDayIndex, setCurrentDayIndex] = useState(0);
	const [loading, setLoading] = useState(true);

	const [loadingProgress, setLoadingProgress] = useState(0);
	const [loadingStep, setLoadingStep] = useState('');

	const [isSubmittingAll, setIsSubmittingAll] = useState(false);
	const [loadingMatchIds, setLoadingMatchIds] = useState(new Set());
	const [showNonMemberModal, setShowNonMemberModal] = useState(false);

	const [rankingsLoading, setRankingsLoading] = useState(false);

	// matchId → { hasStatistics, homeRedCards, awayRedCards } pro zápasy vybraného dne.
	const [statisticsSummaries, setStatisticsSummaries] = useState({});

	// --- AI SKAUT STAVY ---
	const [scoutCredits, setScoutCredits] = useState(null);
	const [isScoutMode, setIsScoutMode] = useState(false);
	// Ukládáme si referenční ID zápasu, pro který jsme zjišťovali kredity (reprezentant hracího dne)
	const [scoutMatchDayMatchId, setScoutMatchDayMatchId] = useState(null);

	// --- ANALÝZA / PREDIKCE STAVY ---
	const [isAnalysisMenuOpen, setIsAnalysisMenuOpen] = useState(false);
	const [isPredictionMode, setIsPredictionMode] = useState(false);
	const [predictionModalMatch, setPredictionModalMatch] = useState(null);
	// Predikce odemčené v rámci této session – aby se re-view ikonka na kartě
	// objevila hned, ještě než backend při dalším načtení vyplní match.predictionUnlocked.
	const [unlockedPredictionIds, setUnlockedPredictionIds] = useState(() => new Set());
	const [isOddsMode, setIsOddsMode] = useState(false);
	const [oddsModalMatch, setOddsModalMatch] = useState(null);
	// Pre-check Analýza → Sázkové kurzy / Predikce čteme přímo z `match.hasOdds`
	// / `match.hasPrediction`, které backend vyplňuje v LeagueController při
	// načtení ligy – žádný extra request.

	const isTooFarInFuture = useMemo(() => {
		if (!selectedDate) return false;
		const diffTime = selectedDate.getTime() - new Date().getTime();
		const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
		return diffDays > 14;
	}, [selectedDate]);

	const isPastDate = useMemo(() => {
		if (!selectedDate) return false;
		const today = new Date();
		today.setHours(0, 0, 0, 0);

		const selected = new Date(selectedDate);
		selected.setHours(0, 0, 0, 0);

		return selected < today;
	}, [selectedDate]);

	const toggleScoutMode = () => {
		if (scoutCredits <= 0 && !isScoutMode) {
			toast.warning(t('leagueDetails.noScoutCredits', { defaultValue: "Dnešní kredity vyčerpány!" }));
			return;
		}
		if (isTooFarInFuture && !isScoutMode) {
			toast.info(t('ai_too_far_future', { defaultValue: "AI Tip bude dostupný 14 dní před zápasem" }));
			return;
		}
		if (isPastDate && !isScoutMode) {
			toast.info(t('ai_past_date', { defaultValue: "Nelze analyzovat zápasy v minulosti" }));
			return;
		}
		if (!isScoutMode) {
			setIsPredictionMode(false);
			setIsOddsMode(false);
		}
		setIsScoutMode(!isScoutMode);
		setIsAnalysisMenuOpen(false);
	};

	const togglePredictionMode = () => {
		// Predikce čerpá stejný sdílený kredit jako AI Skaut. Při vyčerpaném kreditu
		// (0/1) do módu vstoupit nelze – stejně jako u AI Skauta.
		if (!isPredictionMode && scoutCredits <= 0) {
			toast.warning(t('leagueDetails.noScoutCredits', { defaultValue: "Dnešní kredity vyčerpány!" }));
			return;
		}
		if (!isPredictionMode) {
			setIsScoutMode(false);
			setIsOddsMode(false);
		}
		setIsPredictionMode(!isPredictionMode);
		setIsAnalysisMenuOpen(false);
	};

	const toggleOddsMode = () => {
		if (!isOddsMode) {
			setIsScoutMode(false);
			setIsPredictionMode(false);
		}
		setIsOddsMode(!isOddsMode);
		setIsAnalysisMenuOpen(false);
	};

	const handlePredictionMatchSelected = (match) => {
		setPredictionModalMatch(match);
		setIsPredictionMode(false);
	};

	const closePredictionModal = () => {
		setPredictionModalMatch(null);
	};

	const handleOddsMatchSelected = (match) => {
		setOddsModalMatch(match);
		setIsOddsMode(false);
	};

	const closeOddsModal = () => {
		setOddsModalMatch(null);
	};


	// Funkce pro obnovu kreditů po použití
	const handleScoutUsed = () => {
		// Zrušíme scout mód
		setIsScoutMode(false);
		toast.success(t('leagueDetails.scoutUsed', { defaultValue: "Skaut odhalil své tajemství!" }));

		// Aktualizujeme počet kreditů
		if (scoutMatchDayMatchId && league?.tournament?.id) {
			matchService.getRemainingCredits(scoutMatchDayMatchId, league.tournament.id)
				.then(setScoutCredits)
				.catch(() => setScoutCredits(0));
		} else {
			// Fallback - jen odečteme lokálně, pokud selže logika
			setScoutCredits(prev => Math.max(0, (prev || 0) - 1));
		}
	};

	// Po úspěšném zobrazení predikce (čerpá stejný sdílený kredit) jen obnovíme jeho stav.
	// Pokud byla predikce u zápasu už dříve odemčená, server vrátí nezměněný počet.
	// useCallback drží stabilní referenci, aby se efekt v PredictionModalu nespouštěl opakovaně.
	const handlePredictionUsed = useCallback((matchId) => {
		// Označíme zápas jako odemčený, aby se re-view ikonka objevila okamžitě.
		if (matchId != null) {
			setUnlockedPredictionIds(prev => prev.has(matchId) ? prev : new Set(prev).add(matchId));
		}
		if (scoutMatchDayMatchId && league?.tournament?.id) {
			matchService.getRemainingCredits(scoutMatchDayMatchId, league.tournament.id)
				.then(setScoutCredits)
				.catch(() => {});
		}
	}, [scoutMatchDayMatchId, league?.tournament?.id]);

	useEffect(() => {
		const fetchLeague = async () => {
			try {
				setLoading(true);
				setLoadingProgress(0);

				setLoadingStep(t('loadings.fetchingLeague', { defaultValue: 'Načítání informací o lize...' }));
				setLoadingProgress(20);

				// Liga a tipy na sobě nezávisí – načítáme je paralelně, ať se nečeká
				// na dva round-tripy za sebou. setLeague voláme až na konci (kvůli
				// batch update v Reactu).
				const [response, predictionResponse] = await Promise.all([
					leagueService.getLeagueById(leagueId),
					predictionService.getPredictionsByLeagueAndUser(leagueId)
				]);

				setLoadingStep(t('loadings.fetchingPredictions', { defaultValue: 'Načítání tipů...' }));
				setLoadingProgress(50);

				const initialPredictions = {};
				predictionResponse.forEach((prediction) => {
					initialPredictions[prediction.matchId] = {
						predictedHomeScore: prediction.predictedHomeScore,
						predictedAwayScore: prediction.predictedAwayScore,
						id: prediction.id,
						points: prediction.points,
						isDrawInRegularTime: prediction.drawInRegularTime,
						advancingTeamId: prediction.advancingTeamId 
					};
				});
				setPredictions(initialPredictions);
				setLoadingProgress(70);

				setLoadingStep(t('loadings.fetchingRankings', { defaultValue: 'Načítání tabulky...' }));

				setLoadingProgress(90);

				setLeague(response);
				preloadBadges(response.tournament?.matches);

				setLoadingStep(t('loadings.finalizing', { defaultValue: 'Dokončování...' }));
				setLoadingProgress(100);

				setTimeout(() => {
					setLoading(false);
					if (!response.isMember) {
						setShowNonMemberModal(true);
					}
				}, 200);

			} catch (err) {
				console.error(err);
				setError(t('leagueDetails.fetchError', { defaultValue: 'Nepodařilo se načíst data ligy.' }));
				setLoading(false);
			}
		};

		fetchLeague();
	}, [leagueId, t]);

	useEffect(() => {
		const fetchRankings = async () => {
			// Čekáme na načtenou ligu: efekt níže z ní dopočítá aktuální hrací den
			// a nastaví `selectedDate`. Bez této podmínky by při mountu odešel
			// request na celkový žebříček, jehož výsledek by se hned zahodil.
			if (!leagueId || !league) return;

			try {
				setRankingsLoading(true);
				// Pokud selectedDate ještě není nastaveno (null), pošle se null a backend vrátí celkový žebříček
				// Pokud nastaveno je, pošle se datum a backend vrátí i body za daný den
				const rankingsResponse = await leagueService.getLeagueRankings(leagueId, selectedDate);
				setRankings(rankingsResponse);
			} catch (err) {
				console.error("Failed to fetch rankings", err);
			} finally {
				setRankingsLoading(false);
			}
		};

		fetchRankings();
		// `league` je v závislostech jen jako signál „liga je načtená" – vlastní
		// dotaz se řídí `leagueId` a `selectedDate`.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [leagueId, selectedDate, !!league]);

	// Extract unique match dates and filter by league.createdAt
	const uniqueMatchDates = useMemo(() => {
		if (!league) return [];
		const leagueCreationDate = new Date(league.createdAt);
		const dates = league.tournament.matches
			.filter(match => new Date(match.date) >= leagueCreationDate)
			.map(match => {
				const d = new Date(match.date);
				return new Date(d.getFullYear(), d.getMonth(), d.getDate()); // Set time to 00:00:00
			});
		// Remove duplicate dates
		const uniqueDates = Array.from(new Set(dates.map(date => date.getTime()))).map(time => new Date(time));
		// Sort dates
		uniqueDates.sort((a, b) => a - b);
		return uniqueDates;
	}, [league]);

	// Logic for displaying three days in horizontal strip
	const visibleMatchDates = useMemo(() => {
		return uniqueMatchDates.slice(currentDayIndex, currentDayIndex + 3);
	}, [uniqueMatchDates, currentDayIndex]);

	// Functions for moving the strip (zjednodušeno - v MatchDaysStrip se používá setSelectedDate)

	const handleSubmitAllPredictions = async () => {
		if (!league?.isMember) return;
		setIsSubmittingAll(true);
		// Add visual feedback to entire page during bulk submission
		document.body.classList.add('submitting-all-predictions');
		let successCount = 0;
		let errorCount = 0;

		// COMPETITIVE liga: zápasy bez spočítaných bodů za tendence přeskočíme –
		// tip pro ně (za)tím nelze podat (stejný guard je i na backendu).
		const isCompetitiveLeague = (league.scoringType || '').toUpperCase() === 'COMPETITIVE';

		const matchesToSubmit = filteredMatches.filter(match => {
			const pred = predictions[match.id];
			const matchDate = new Date(match.date);
			const isFutureMatch = matchDate > now;

			if (isCompetitiveLeague
				&& (typeof match.homeTeamPoints !== 'number'
					|| typeof match.awayTeamPoints !== 'number'
					|| typeof match.drawPoints !== 'number')) {
				return false;
			}

			return isFutureMatch &&
				pred &&
				pred.predictedHomeScore !== undefined &&
				pred.predictedAwayScore !== undefined &&
				pred.predictedHomeScore !== '' &&
				pred.predictedAwayScore !== '';
		});

		trackEvent('Bulk Predictions Started', {
			count: matchesToSubmit.length,
			sport: league.tournament.sportType,
			league_visibility: league.visibility
		});

		for (const match of matchesToSubmit) {
			const pred = predictions[match.id];
			const isFootball = (match.sportType || match.homeTeam?.sportType || league?.tournament?.sportType)?.toUpperCase() === 'FOOTBALL';
			const isSecondLeg = match.secondLeg || match.isSecondLeg;

			let isAggregateDraw = false;
			if (isFootball && isSecondLeg) {
				const firstLegMatch = league.tournament.matches.find(m => 
					m.round === match.round && m.homeTeam?.id === match.awayTeam?.id && 
					m.awayTeam?.id === match.homeTeam?.id && new Date(m.date) < new Date(match.date) &&
					m.scoreHome !== null && m.scoreAway !== null
				);
	
				if (firstLegMatch) {
					const aggHome = firstLegMatch.scoreAway + parseInt(pred.predictedHomeScore, 10);
					const aggAway = firstLegMatch.scoreHome + parseInt(pred.predictedAwayScore, 10);
					if (aggHome === aggAway) {
						isAggregateDraw = true;
					}
				}
			}

			if (isAggregateDraw && !pred.advancingTeamId) {
				toast.error(t('leagueDetails.selectAdvancingTeamForMatch', { defaultValue: `Celkové skóre je remíza! Vyberte postupujícího u zápasu ${match.homeTeam.name} - ${match.awayTeam.name}` }));
				errorCount++;
				continue;
			}

			if (!isFootball && match.canGoToOvertime && parseInt(pred.predictedHomeScore) === parseInt(pred.predictedAwayScore)) {
				toast.error(t('leagueDetails.noDrawAllowedForOvertime') + ` (${match.homeTeam.name} - ${match.awayTeam.name})`);
				errorCount++;
				continue;
			}

			if (!isFootball && pred.isDrawInRegularTime && Math.abs(pred.predictedHomeScore - pred.predictedAwayScore) > 1) {
				toast.error(t('leagueDetails.drawTipInvalid') + ` (${match.homeTeam.name} - ${match.awayTeam.name})`);
				errorCount++;
				continue;
			}

			const isSingleLegOvertime = isFootball && !isSecondLeg && match.canGoToOvertime;
			const isSingleLegOvertimeDraw = isSingleLegOvertime
				&& parseInt(pred.predictedHomeScore, 10) === parseInt(pred.predictedAwayScore, 10);
			const sendAdvancingTeam = isAggregateDraw || isSingleLegOvertimeDraw;

			const payload = {
				leagueId,
				matchId: match.id,
				predictedHomeScore: pred.predictedHomeScore,
				predictedAwayScore: pred.predictedAwayScore,
				// Remíza po 90 min (jednorázový zápas) i remíza na agregát (odveta) znamenají
				// prodloužení – příznak proto posíláme stejně jako výběr postupujícího týmu.
				// U jednorázového fotbalového zápasu se příznak odvozuje čistě z tipnutého skóre,
				// aby po změně remízy na výhru nezůstal viset true z dřívějšího tipu.
				isDrawInRegularTime: sendAdvancingTeam ? true : (isSingleLegOvertime ? false : !!pred.isDrawInRegularTime),
				advancingTeamId: sendAdvancingTeam ? (pred.advancingTeamId || null) : null
			};

			try {
				let updatedPrediction;
				if (pred.id) {
					updatedPrediction = await predictionService.updatePrediction(payload);
				} else {
					updatedPrediction = await predictionService.addPrediction(payload);
				}

				setPredictions(prev => ({
					...prev,
					[match.id]: {
						...prev[match.id],
						id: updatedPrediction.id,
						predictedHomeScore: updatedPrediction.predictedHomeScore,
						predictedAwayScore: updatedPrediction.predictedAwayScore,
						isDrawInRegularTime: payload.isDrawInRegularTime,
						advancingTeamId: updatedPrediction.advancingTeamId || null
					}
				}));

				successCount++;
			} catch (err) {
				toast.error(t('leagueDetails.predictionFailed') + ` (${match.homeTeam.name} - ${match.awayTeam.name})`);
				errorCount++;
			}
		}

		setIsSubmittingAll(false);
		// Remove visual feedback from page
		document.body.classList.remove('submitting-all-predictions');

		trackEvent('Bulk Predictions Completed', {
			total_count: matchesToSubmit.length,
			success_count: successCount,
			error_count: errorCount,
			sport: league.tournament.sportType,
			league_visibility: league.visibility
		});

		// Show summary toast
		if (successCount > 0 && errorCount === 0) {
			toast.success(t('leagueDetails.predictionsSaved') + ` (${successCount})`);
		} else if (successCount > 0 && errorCount > 0) {
			toast.warning(`${successCount} ${t('leagueDetails.predictionsSaved')}, ${errorCount} ${t('leagueDetails.predictionsFailed')}`);
		} else if (errorCount > 0) {
			toast.error(`${t('leagueDetails.allPredictionsFailed')} (${errorCount})`);
		}
	};

	// Updated useEffect for selecting the closest day in the strip
	useEffect(() => {
		if (!league || uniqueMatchDates.length === 0) {
			setSelectedDate(null);
			return;
		}

		// Current date
		const now = new Date();
		// Set time to beginning of day to compare dates only
		now.setHours(0, 0, 0, 0);

		// Check if there's a match today
		const todayMatch = uniqueMatchDates.find(date =>
			date.getFullYear() === now.getFullYear() &&
			date.getMonth() === now.getMonth() &&
			date.getDate() === now.getDate()
		);

		// If there's a match today, select today's date
		if (todayMatch) {
			setSelectedDate(todayMatch);
			// Find index of today in uniqueMatchDates array
			const todayIndex = uniqueMatchDates.findIndex(date =>
				date.getTime() === todayMatch.getTime()
			);
			if (todayIndex > -1) {
				// Set strip index so today is visible
				setCurrentDayIndex(Math.max(0, todayIndex - 1));
			}
		}
		// Otherwise look for the nearest future date
		else {
			// Find the nearest upcoming date
			const upcomingMatch = uniqueMatchDates
				.filter(date => date > now)
				.sort((a, b) => a - b)[0]; // Nearest future date

			if (upcomingMatch) {
				setSelectedDate(upcomingMatch);
				// Set current calendar index
				const upcomingIndex = uniqueMatchDates.findIndex(date =>
					date.getTime() === upcomingMatch.getTime()
				);
				if (upcomingIndex > -1) {
					setCurrentDayIndex(Math.max(0, upcomingIndex - 1)); // Show previous and next days
				}
			}
			// If no future match exists, take the nearest past one
			else {
				const pastMatch = uniqueMatchDates
					.filter(date => date <= now)
					.sort((a, b) => b - a)[0]; // Nearest past date

				if (pastMatch) {
					setSelectedDate(pastMatch);
					const pastIndex = uniqueMatchDates.findIndex(date =>
						date.getTime() === pastMatch.getTime()
					);
					if (pastIndex > -1) {
						setCurrentDayIndex(Math.max(0, pastIndex - 1));
					}
				} else {
					// If no match exists, select first day in calendar
					setSelectedDate(null);
				}
			}
		}
	}, [league, uniqueMatchDates]);

	useEffect(() => {
		const fetchCreditsForSelectedDate = async () => {
			// Potřebujeme mít vybrané datum, načtenou ligu a být členy
			if (!selectedDate || !league || !league.isMember || !league.tournament) return;

			setScoutCredits(null); // Nastavíme loading stav (tlačítko zešedne a ukáže ...)

			// Najdeme libovolný zápas z vybraného dne, který použijeme jako "klíč" k hracímu dni
			const matchOnSelectedDate = league.tournament.matches.find(m => {
				const mDate = new Date(m.date);
				return mDate.getDate() === selectedDate.getDate() &&
					mDate.getMonth() === selectedDate.getMonth() &&
					mDate.getFullYear() === selectedDate.getFullYear();
			});

			if (matchOnSelectedDate) {
				setScoutMatchDayMatchId(matchOnSelectedDate.id);
				try {
					const credits = await matchService.getRemainingCredits(matchOnSelectedDate.id, league.tournament.id);
					setScoutCredits(credits);
				} catch (error) {
					console.error("Failed to load credits for date", selectedDate, error);
					setScoutCredits(0);
				}
			} else {
				// Pokud pro daný den není zápas (divné, ale možné), nemáme kredity
				setScoutCredits(0);
			}
		};

		fetchCreditsForSelectedDate();
	}, [selectedDate, league]);

	// Filter matches by selected date and league.createdAt
	const filteredMatches = useMemo(() => {
		if (!league) return [];
		const leagueCreationDate = new Date(league.createdAt);
		let matches = league.tournament.matches.filter(match => new Date(match.date) >= leagueCreationDate);

		if (selectedDate) {
			matches = matches.filter(match => {
				const matchDate = new Date(match.date);
				return (
					matchDate.getFullYear() === selectedDate.getFullYear() &&
					matchDate.getMonth() === selectedDate.getMonth() &&
					matchDate.getDate() === selectedDate.getDate()
				);
			});
		}

		// Sort matches by start time
		matches.sort((a, b) => {
			const dateA = new Date(a.date);
			const dateB = new Date(b.date);
			if (dateA.getTime() !== dateB.getTime()) {
				return dateA - dateB;
			}
			return a.id - b.id;
		});
		return matches;
	}, [league, selectedDate]);

	// Souhrn statistik pro zápasy vybraného dne – jeden request místo volání
	// /statistics na každé kartě zvlášť. Backend vrátí jen zápasy, pro které data
	// existují; karta z toho bere dostupnost tlačítka i počty červených karet.
	useEffect(() => {
		let cancelled = false;
		const matchIds = filteredMatches.map(m => m.id);
		if (matchIds.length === 0) return;

		matchService.getMatchStatisticsSummary(matchIds).then(summaries => {
			if (cancelled) return;
			setStatisticsSummaries(prev => {
				const next = { ...prev };
				summaries.forEach(summary => { next[summary.matchId] = summary; });
				return next;
			});
		});

		return () => { cancelled = true; };
	}, [filteredMatches]);


	// Pokud žádný zápas vybraného dne nemá kurzy / predikce, položka v dropdownu
	// Analýza je disabled – uživatel by se stejně nikam neproklikl.
	// Kurzy lze otevřít jen u zápasů, které ještě nezačaly (overlay v MatchCard se
	// u odehraných/rozehraných zápasů nezobrazuje) – proto musí den obsahovat
	// alespoň jeden budoucí zápas s kurzy, jinak je položka disabled.
	const dayHasAnyOdds = useMemo(
		() => {
			const now = new Date();
			return filteredMatches.some(m => m.hasOdds === true && new Date(m.date) >= now);
		},
		[filteredMatches]
	);
	const dayHasAnyPrediction = useMemo(
		() => filteredMatches.some(m => m.hasPrediction === true),
		[filteredMatches]
	);

	// Dostupnost jednotlivých položek v dropdownu Analýza. Pokud nelze použít
	// žádnou z nich, je disabled i samotné tlačítko Analýza (dokud se načítá
	// kredit, tlačítko necháváme aktivní – stav Skauta/Predikce ještě neznáme).
	const isScoutItemDisabled = scoutCredits === null || scoutCredits === 0 || isTooFarInFuture || isPastDate;
	const isPredictionItemDisabled = scoutCredits === null || scoutCredits === 0 || !dayHasAnyPrediction;
	const isOddsItemDisabled = !dayHasAnyOdds;
	const isAnalysisMenuDisabled = scoutCredits !== null
		&& isScoutItemDisabled && isPredictionItemDisabled && isOddsItemDisabled;

	// Přepnutí na den bez použitelných kurzů (např. přes strip dnů) režim kurzů ukončí,
	// aby uživatel nezůstal v módu, ve kterém nelze nic vybrat.
	useEffect(() => {
		if (isOddsMode && !dayHasAnyOdds) {
			setIsOddsMode(false);
		}
	}, [isOddsMode, dayHasAnyOdds]);

	// Otevřený dropdown zavřeme, jakmile v něm nezbude žádná použitelná položka.
	useEffect(() => {
		if (isAnalysisMenuOpen && isAnalysisMenuDisabled) {
			setIsAnalysisMenuOpen(false);
		}
	}, [isAnalysisMenuOpen, isAnalysisMenuDisabled]);

	// Turnaj jednoho týmu (např. "AC Sparta Praha 2026-2027") – jeden tým hraje ve všech
	// zápasech, které ale pochází z různých soutěží (Chance liga, Liga mistrů, …).
	// U takového turnaje doplňujeme za název kola i soutěž, ve které se zápas hraje.
	const isSingleTeamTournament = useMemo(() => {
		const tournamentMatches = league?.tournament?.matches || [];
		if (tournamentMatches.length === 0) return false;
		return [tournamentMatches[0].homeTeam?.id, tournamentMatches[0].awayTeam?.id]
			.filter(Boolean)
			.some((teamId) =>
				tournamentMatches.every(
					(m) => m.homeTeam?.id === teamId || m.awayTeam?.id === teamId
				)
			);
	}, [league]);

	const displayRounds = useMemo(() => {
		if (!filteredMatches || filteredMatches.length === 0) return [];

		const entries = filteredMatches
			.filter(match => match.round !== null && match.round !== undefined && match.round !== '')
			.map(match => ({
				round: match.round,
				leagueName: isSingleTeamTournament ? (match.leagueName || null) : null,
			}));

		const seen = new Set();
		const uniqueEntries = entries.filter(entry => {
			const key = `${entry.round}|${entry.leagueName ?? ''}`;
			if (seen.has(key)) return false;
			seen.add(key);
			return true;
		});

		const allNumeric = uniqueEntries.every(entry => isNumericRound(entry.round));
		if (allNumeric) {
			return [...uniqueEntries].sort((a, b) => Number(a.round) - Number(b.round));
		}
		// For string-based (football) rounds keep insertion order (matches are date-sorted).
		return uniqueEntries;
	}, [filteredMatches, isSingleTeamTournament]);

	// Mapa round → multiplier (bodový násobič kola). Backend ho posílá na každém
	// zápase (MatchDto.multiplier, default 1). Použije se pro label „(Body: N×)"
	// za názvem kola, když je násobič > 1.
	const roundMultipliers = useMemo(() => {
		const map = {};
		(filteredMatches || []).forEach(match => {
			if (match.round === null || match.round === undefined || match.round === '') return;
			const m = Number(match.multiplier);
			if (Number.isFinite(m) && m > (map[match.round] ?? 1)) {
				map[match.round] = m;
			}
		});
		return map;
	}, [filteredMatches]);

	// Handle input changes
	const handleInputChange = (matchId, field, value) => {
		const scrollY = window.scrollY;

		setPredictions((prev) => ({
			...prev,
			[matchId]: {
				...prev[matchId],
				[field]: value,
			},
		}));

		// Scroll back after microtask
		setTimeout(() => {
			window.scrollTo({ top: scrollY });
		}, 0);
	};

	const validFutureTipsCount = filteredMatches.filter(m => {
		const pred = predictions[m.id];
		const matchDate = new Date(m.date);
		const isFutureMatch = matchDate > now;

		return isFutureMatch &&
			pred &&
			pred.predictedHomeScore !== undefined &&
			pred.predictedAwayScore !== undefined &&
			pred.predictedHomeScore !== '' &&
			pred.predictedAwayScore !== '';
	}).length;

	const handleTip = async (matchId) => {
		if (!league?.isMember) return;
		setLoadingMatchIds(prev => new Set([...prev, matchId]));

		const scrollY = window.scrollY;
		const prediction = predictions[matchId];
		const match = league?.tournament?.matches.find(m => m.id === matchId);
		const isFootball = (match.sportType || match.homeTeam?.sportType || league?.tournament?.sportType)?.toUpperCase() === 'FOOTBALL';
		const isSecondLeg = match.secondLeg || match.isSecondLeg;
		
		try {
			let isAggregateDraw = false;
			if (isFootball && isSecondLeg) {
				const firstLegMatch = league.tournament.matches.find(m => 
					m.round === match.round && 
					m.homeTeam?.id === match.awayTeam?.id && 
					m.awayTeam?.id === match.homeTeam?.id && 
					new Date(m.date) < new Date(match.date) &&
					m.scoreHome !== null && m.scoreAway !== null
				);
	
				if (firstLegMatch) {
					const aggHome = firstLegMatch.scoreAway + parseInt(prediction.predictedHomeScore, 10);
					const aggAway = firstLegMatch.scoreHome + parseInt(prediction.predictedAwayScore, 10);
					if (aggHome === aggAway) {
						isAggregateDraw = true;
					}
				}
			}

			if (isAggregateDraw && !prediction.advancingTeamId) {
				toast.error(t('leagueDetails.selectAdvancingTeam', { defaultValue: 'Celkové skóre je remíza! Musíte vybrat postupujícího tým.' }));
				setLoadingMatchIds(prev => { const newSet = new Set(prev); newSet.delete(matchId); return newSet; });
				return;
			}

			// Původní hokejová validace a validace prázdných gólů...
			if (prediction?.isDrawInRegularTime && !(isFootball && isSecondLeg)) {
				const home = parseInt(prediction.predictedHomeScore, 10);
				const away = parseInt(prediction.predictedAwayScore, 10);

				if (isNaN(home) || isNaN(away)) {
					toast.error(t('leagueDetails.fillBothPredictions'));
					setLoadingMatchIds(prev => { const newSet = new Set(prev); newSet.delete(matchId); return newSet; });
					return;
				}
				if (!isFootball && Math.abs(home - away) > 1) {
					toast.error(t('leagueDetails.drawTipInvalid'));
					setLoadingMatchIds(prev => { const newSet = new Set(prev); newSet.delete(matchId); return newSet; });
					return;
				}
			}

			if (
				prediction?.predictedHomeScore === undefined ||
				prediction?.predictedAwayScore === undefined ||
				prediction?.predictedHomeScore === '' ||
				prediction?.predictedAwayScore === ''
			) {
				toast.error(t('leagueDetails.fillBothPredictions'));
				setLoadingMatchIds(prev => { const newSet = new Set(prev); newSet.delete(matchId); return newSet; });
				return;
			}

			if (match?.canGoToOvertime && !isFootball) {
				const home = parseInt(prediction.predictedHomeScore, 10);
				const away = parseInt(prediction.predictedAwayScore, 10);

				if (!isFootball && home === away) {
					toast.error(t('leagueDetails.noDrawAllowedForOvertime'));
					setLoadingMatchIds(prev => { const newSet = new Set(prev); newSet.delete(matchId); return newSet; });
					return;
				}
			}

			const isSingleLegOvertime = isFootball && !isSecondLeg && match.canGoToOvertime;
			const isSingleLegOvertimeDraw = isSingleLegOvertime
				&& parseInt(prediction.predictedHomeScore, 10) === parseInt(prediction.predictedAwayScore, 10);
			const sendAdvancingTeam = isAggregateDraw || isSingleLegOvertimeDraw;

			const payload = {
				leagueId: leagueId,
				matchId: matchId,
				predictedHomeScore: prediction.predictedHomeScore,
				predictedAwayScore: prediction.predictedAwayScore,
				// Remíza po 90 min (jednorázový zápas) i remíza na agregát (odveta) znamenají
				// prodloužení – příznak proto posíláme stejně jako výběr postupujícího týmu.
				// U jednorázového fotbalového zápasu se příznak odvozuje čistě z tipnutého skóre,
				// aby po změně remízy na výhru nezůstal viset true z dřívějšího tipu.
				isDrawInRegularTime: sendAdvancingTeam ? true : (isSingleLegOvertime ? false : !!prediction.isDrawInRegularTime),
				advancingTeamId: sendAdvancingTeam ? (prediction.advancingTeamId || null) : null
			};

			let updatedPrediction;
			const isUpdate = Boolean(prediction.id);
			const toastMessage = (
				<>
					{t(isUpdate ? 'leagueDetails.predictionUpdated' : 'leagueDetails.predictionSubmitted')}
					<br />
					(
					{t(`teams.${match.homeTeam.name}`, { defaultValue: match.homeTeam.name })}{' '}
					{prediction.predictedHomeScore}
					{prediction.isDrawInRegularTime && prediction.predictedHomeScore > prediction.predictedAwayScore ? 'x' : ''}
					:
					{prediction.predictedAwayScore}
					{prediction.isDrawInRegularTime && prediction.predictedAwayScore > prediction.predictedHomeScore ? 'x' : ''}{' '}
					{t(`teams.${match.awayTeam.name}`, { defaultValue: match.awayTeam.name })}
					)
				</>
			);

			if (isUpdate) {
				updatedPrediction = await predictionService.updatePrediction(payload);
			} else {
				updatedPrediction = await predictionService.addPrediction(payload);
			}

			trackEvent('Prediction Submitted', {
				sport: match.sportType || match.homeTeam.sportType,
				type: 'individual',
				is_update: isUpdate,
				has_overtime_bet: Boolean(payload.isDrawInRegularTime),
				league_visibility: league.visibility
			});

			toast.success(toastMessage);

			setPredictions((prev) => ({
				...prev,
				[matchId]: {
					...prev[matchId],
					id: updatedPrediction.id,
					predictedHomeScore: updatedPrediction.predictedHomeScore,
					predictedAwayScore: updatedPrediction.predictedAwayScore,
					isDrawInRegularTime: payload.isDrawInRegularTime,
					advancingTeamId: updatedPrediction.advancingTeamId || null
				},
			}));

			setTimeout(() => {
				requestAnimationFrame(() => {
					window.scrollTo({ top: scrollY, behavior: 'auto' });
				});
			}, 0);
		} catch (err) {
			trackEvent('Prediction Failed', {
				sport: match.sportType || match.homeTeam.sportType,
				type: 'individual',
				error: err.response?.status || 'unknown'
			});
			toast.error(t('leagueDetails.predictionFailed'));
		} finally {
			// Remove loading state for this match
			setLoadingMatchIds(prev => {
				const newSet = new Set(prev);
				newSet.delete(matchId);
				return newSet;
			});
		}
	};

	// Handle join league action
	const handleJoinLeague = async () => {
		try {
			await leagueService.joinLeague(leagueId);
			setLeague(prev => ({ ...prev, isMember: true }));
			setShowNonMemberModal(false);
			toast.success(t('leagueDetails.successfullyJoinedLeague', { defaultValue: 'Úspěšně jste se připojili do ligy!' }));
		} catch (err) {
			toast.error(t('leagueDetails.failedToJoinLeague', { defaultValue: 'Nepodařilo se připojit do ligy' }));
		}
	};

	if (loading) {
		return (
			<div className="leagues-container">
				<div className="leagues-loading">
					<div className="loading-content">
						<div className="loading-info">
							<p className="loading-text">{loadingStep}</p>
							<div className="progress-bar">
								<div
									className="progress-fill"
									style={{ width: `${loadingProgress}%` }}
								></div>
							</div>
							<p className="progress-percentage">{loadingProgress}%</p>
						</div>
					</div>
				</div>
			</div>
		);
	}

	// --- ZDE JE HLAVNÍ OPRAVA: Kontrola, zda existuje objekt league ---
	if (!league) {
		return (
			<div className="leagues-container">
				<div style={{
					padding: '40px',
					textAlign: 'center',
					backgroundColor: 'white',
					borderRadius: '8px',
					marginTop: '20px',
					boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
				}}>
					<h2>{t('leagueDetails.notFoundTitle', { defaultValue: 'Liga nenalezena' })}</h2>
					<p className="error-message" style={{ margin: '20px 0' }}>
						{error || t('leagueDetails.notFoundMessage', { defaultValue: 'Požadovaná liga neexistuje nebo k ní nemáte přístup.' })}
					</p>
					<Button
						variant="contained"
						color="primary"
						onClick={() => navigate('/dashboard')}
					>
						{t('leagueDetails.backToDashboard', { defaultValue: 'Zpět na přehled' })}
					</Button>
				</div>
			</div>
		);
	}

	const pageTitle = league?.name
		? `${league.name}`
		: t('leagueDetails.title', { defaultValue: "Detail ligy" });

	return (
		<div className={`league-details-wrapper ${isScoutMode ? 'scout-mode-active' : ''} ${isPredictionMode ? 'prediction-mode-active' : ''} ${isOddsMode ? 'odds-mode-active' : ''}`}>

			<SEO
				title={pageTitle}
				description={league?.description || t('leagueDetails.seoDescription', { defaultValue: "Sleduj výsledky, tabulky a tipy v této lize." })}
			/>

			{/* MUI Non-member Dialog */}
			<Dialog
			    open={showNonMemberModal}
			    onClose={() => setShowNonMemberModal(false)}
			    maxWidth="sm"
			    fullWidth
			    scroll="paper" 
			    sx={{
			        '& .MuiBackdrop-root': {
			            backgroundColor: 'rgba(0, 0, 0, 0.5)',
			            backdropFilter: 'blur(3px)', 
			        },
			        '& .MuiDialog-paper': {
			            borderRadius: 2,
			            borderTop: '5px solid #002855',
			            m: 2,
			        }
			    }}
			>
			    <DialogTitle sx={{ 
			        m: 0, 
			        p: 2, 
			        pb: 1, 
			        fontWeight: 700,
			        fontSize: isMobile ? '1.1rem' : '1.25rem'
			    }}>
			        {t('leagueDetails.notAMemberTitle', { defaultValue: 'Nejste členem ligy' })}
			        <IconButton
			            aria-label="close"
			            onClick={() => setShowNonMemberModal(false)}
			            sx={{
			                position: 'absolute',
			                right: 8,
			                top: 8,
			                color: (theme) => theme.palette.grey[500],
			            }}
			        >
			            <CloseIcon />
			        </IconButton>
			    </DialogTitle>
			    <DialogContent sx={{ pt: 1 }}>
			        <Typography variant="body1" sx={{ 
			            mb: 1,
			            fontSize: isMobile ? '0.9rem' : '1rem' 
			        }}>
			            {t('leagueDetails.notAMemberMessage', {
			                defaultValue: 'Abyste mohli posílat tipy a účastnit se této ligy, musíte se nejprve připojit jako člen.'
			            })}
			        </Typography>
			        {league?.visibility === 'PUBLIC' && (
			            <Typography variant="body2" color="text.secondary" sx={{
			                fontSize: isMobile ? '0.8rem' : '0.875rem'
			            }}>
			                {t('leagueDetails.publicLeagueJoinMessage', {
			                    defaultValue: 'Tato liga je veřejná - můžete se připojit kliknutím na tlačítko níže.'
			                })}
			            </Typography>
			        )}
			    </DialogContent>
			    <DialogActions sx={{ p: 2, pt: 1 }}>
			        {league?.visibility === 'PUBLIC' ? (
			            <>
			                <Button
			                    onClick={() => setShowNonMemberModal(false)}
			                    variant="outlined"
			                    size={isMobile ? "small" : "medium"}
			                    sx={{ 
			                        mr: 1,
			                        color: '#2563eb', 
			                        borderColor: '#2563eb',
			                        fontSize: isMobile ? '0.75rem' : '0.875rem',
			                        '&:hover': {
			                            borderColor: '#1d4ed8',
			                            backgroundColor: 'rgba(37, 99, 235, 0.04)'
			                        }
			                    }}
			                >
			                    {t('common.cancel', { defaultValue: 'Pokračovat jako host' })}
			                </Button>
			                <Button
			                    onClick={handleJoinLeague}
			                    size={isMobile ? "small" : "medium"}
			                    variant="contained"
			                    autoFocus
			                    sx={{ 
			                        backgroundColor: '#002855',
			                        fontSize: isMobile ? '0.75rem' : '0.875rem',
			                        '&:hover': {
			                            backgroundColor: '#002044'
			                        }
			                    }}
			                >
			                    {t('leagueDetails.joinLeague', { defaultValue: 'Připojit se do ligy' })}
			                </Button>
			            </>
			        ) : (
			            <>
			                <Button
			                    onClick={() => setShowNonMemberModal(false)}
			                    size={isMobile ? "small" : "medium"}
			                    variant="outlined"
			                    sx={{ 
			                        mr: 1,
			                        color: '#2563eb',
			                        fontSize: isMobile ? '0.75rem' : '0.875rem',
			                        borderColor: '#002855',
			                        '&:hover': {
			                            borderColor: '#002855',
			                            backgroundColor: 'rgba(37, 99, 235, 0.04)'
			                        }
			                    }}
			                >
			                    {t('leagueDetails.browseAsGuest', { defaultValue: 'Pokračovat jako host' })}
			                </Button>
			                <Button
			                    component={Link}
			                    to="/public-leagues"
			                    size={isMobile ? "small" : "medium"}
			                    variant="contained"
			                    sx={{ 
			                        backgroundColor: '#2563eb',
			                        fontSize: isMobile ? '0.75rem' : '0.875rem',
			                        '&:hover': {
			                            backgroundColor: '#1d4ed8'
			                        }
			                    }}
			                >
			                    {t('leagueDetails.backToLeagues', { defaultValue: 'Zpět na ligy' })}
			                </Button>
			            </>
			        )}
			    </DialogActions>
			</Dialog>

			<LeagueHeader
				league={league}
				userName={userName}
			/>

			{/* Horizontal strip with match days */}
			<MatchDaysStrip
				allDates={uniqueMatchDates}
				currentDayIndex={currentDayIndex}
				setCurrentDayIndex={setCurrentDayIndex}
				selectedDate={selectedDate}
				setSelectedDate={setSelectedDate}
				league={league}
			/>

			<div className="action-buttons-container" style={{ display: 'flex', justifyContent: 'center', gap: '15px', margin: '15px 0' }}>

				<button
					className={`tip-button submit-all-button ${isSubmittingAll ? 'loading' : ''}`}
					onClick={handleSubmitAllPredictions}
					disabled={!league?.isMember || validFutureTipsCount === 0 || isSubmittingAll || isScoutMode || isPredictionMode || isOddsMode}
				>
					<div className="tip-button-content">
						{isSubmittingAll && <span className="button-spinner"></span>}
						<span>
							{validFutureTipsCount > 0
								? `${t('leagueDetails.sendAllTips')} (${validFutureTipsCount})`
								: t('leagueDetails.noTipsToSend')}
						</span>
					</div>
				</button>

				{league?.isMember && (
					<div className="analysis-menu-wrapper">
						{(isScoutMode || isPredictionMode || isOddsMode) ? (
							<button
								className={`scout-toggle-button active`}
								onClick={() => {
									setIsScoutMode(false);
									setIsPredictionMode(false);
									setIsOddsMode(false);
									setIsAnalysisMenuOpen(false);
								}}
								disabled={isSubmittingAll}
							>
								<FaTimes className="scout-icon" />
								<span className="scout-text">{t('cancel', { defaultValue: "Zrušit" })}</span>
							</button>
						) : (
							<button
								className={`scout-toggle-button analysis-button ${isAnalysisMenuOpen ? 'open' : ''} ${isAnalysisMenuDisabled ? 'disabled' : ''}`}
								onClick={() => setIsAnalysisMenuOpen(!isAnalysisMenuOpen)}
								disabled={isSubmittingAll || isAnalysisMenuDisabled}
								title={isAnalysisMenuDisabled
									? t('analysis_unavailable_for_day', { defaultValue: "Pro vybraný den nejsou dostupné žádné nástroje analýzy" })
									: t('analysis_button_title', { defaultValue: "Otevřít nástroje analýzy" })}
							>
								<FaChartLine className="scout-icon" />
								<span className="scout-text">{t('analysis', { defaultValue: 'Analýza' })}</span>
								{isAnalysisMenuOpen ? <FaChevronUp className="scout-icon" /> : <FaChevronDown className="scout-icon" />}
							</button>
						)}

						{isAnalysisMenuOpen && !isScoutMode && !isPredictionMode && !isOddsMode && (
							<div className="analysis-dropdown">
								<button
									className={`analysis-dropdown-item ${((scoutCredits === 0 || isTooFarInFuture || isPastDate)) ? 'disabled' : ''}`}
									onClick={toggleScoutMode}
									disabled={isScoutItemDisabled}
									title={
										isPastDate
											? t('ai_past_date_tooltip', { defaultValue: "Nelze analyzovat zápasy v minulosti" })
											: isTooFarInFuture
												? t('ai_too_far_future', { defaultValue: "AI Tip bude dostupný 14 dní před zápasem" })
												: scoutCredits === 0
													? t('scout_limit_exhausted', { defaultValue: "Byl vyčerpán limit pro tento hrací den" })
													: t('activate_scout', { defaultValue: "Aktivovat Skauta" })
									}
								>
									<FaRobot className="scout-icon" />
									<span>
										{`${t('aiScout', { defaultValue: 'AI Skaut' })} (${scoutCredits === null ? '...' : `${scoutCredits}/1`})`}
									</span>
								</button>
								<button
									className={`analysis-dropdown-item ${(!dayHasAnyPrediction || scoutCredits === 0) ? 'disabled' : ''}`}
									onClick={togglePredictionMode}
									disabled={isPredictionItemDisabled}
									title={!dayHasAnyPrediction
										? t('predictionUnavailableForDay', { defaultValue: 'Pro vybraný den nejsou dostupné žádné predikce' })
										: scoutCredits === 0
											? t('scout_limit_exhausted', { defaultValue: "Byl vyčerpán limit pro tento hrací den" })
											: undefined}
								>
									<FaChartLine className="scout-icon" />
									<span>
										{`${t('predictions', { defaultValue: 'Predikce' })} (${scoutCredits === null ? '...' : `${scoutCredits}/1`})`}
									</span>
								</button>
								<button
									className={`analysis-dropdown-item ${!dayHasAnyOdds ? 'disabled' : ''}`}
									onClick={toggleOddsMode}
									disabled={isOddsItemDisabled}
									title={!dayHasAnyOdds
										? t('oddsUnavailableForDay', { defaultValue: 'Pro vybraný den nejsou dostupné žádné kurzy' })
										: undefined}
								>
									<FaCoins className="scout-icon" />
									<span>{t('odds', { defaultValue: 'Sázkové kurzy' })}</span>
								</button>
							</div>
						)}
					</div>
				)}
			</div>

			{error && <p style={{ color: 'red', textAlign: 'center' }}>{error}</p>}
			
			{displayRounds.length > 0 && (
				<div style={{ 
					textAlign: 'center', 
					color: '#6b7280',
					fontSize: '0.7rem', 
					marginBottom: '-15px',
					fontWeight: 400,
					textTransform: 'uppercase',
					letterSpacing: '0.5px'
				}}>
					{displayRounds
						.map(({ round, leagueName }) => {
							const label = translateRound(round, league?.tournament?.name, t);
							const m = roundMultipliers[round] ?? 1;
							const details = [
								leagueName ? translateLeague(leagueName, t) : null,
								m > 1 ? t('leagueDetails.roundMultiplier', { multiplier: m }) : null,
							].filter(Boolean);
							return details.length > 0 ? `${label} (${details.join(', ')})` : label;
						})
						.join(' / ')}
				</div>
			)}

			{/* Display matches */}
			<div
				key={selectedDate ? selectedDate.getTime() : 'all'}
				className={`matches-container ${filteredMatches.length === 1 ? 'single-column' : 'two-columns'}`}
			>
				{filteredMatches.map((match, index) => {
					
					const firstLegMatch = league.tournament.matches.find(m => 
						m.round && match.round && m.round === match.round && // Zápas ze stejného kola
						m.homeTeam?.id === match.awayTeam?.id && // Domácí hráli venku
						m.awayTeam?.id === match.homeTeam?.id && // Hosté hráli doma
						new Date(m.date) < new Date(match.date) && // Tento zápas se hrál dříve
						m.scoreHome !== null && m.scoreAway !== null // Zápas už má zadané skóre
					);
					
					return (
						<MatchCard
							key={match.id}
							match={match}
							firstLegMatch={firstLegMatch}
							prediction={predictions[match.id]}
							isPast={new Date(match.date) < new Date()}
							onInputChange={(field, value) => handleInputChange(match.id, field, value)}
							onSubmitPrediction={() => handleTip(match.id)}
							isLoading={loadingMatchIds.has(match.id)}
							isDisabled={isSubmittingAll}
							isScoutMode={isScoutMode}
							onScoutUsed={handleScoutUsed}
							isPredictionMode={isPredictionMode}
							onPredictionSelected={handlePredictionMatchSelected}
							hasPrediction={match.hasPrediction === true}
							predictionUnlocked={match.predictionUnlocked === true || unlockedPredictionIds.has(match.id)}
							canUsePredictionCredit={scoutCredits > 0}
							isOddsMode={isOddsMode}
							onOddsSelected={handleOddsMatchSelected}
							hasOdds={match.hasOdds === true}
							tournamentId={league.tournament.id}
							isInternational={league.tournament?.international}
							isMember={league.isMember}
							scoringType={league.scoringType}
							statisticsSummary={statisticsSummaries[match.id] || null}
							leagueId={league.id}
						/>
					);
				})}
			</div>

			<RankingsTable
				rankings={rankings}
				league={league}
				selectedDate={selectedDate}
			/>

			<PredictionModal
				open={!!predictionModalMatch}
				matchId={predictionModalMatch?.id}
				tournamentId={league?.tournament?.id}
				homeTeam={predictionModalMatch?.homeTeam}
				awayTeam={predictionModalMatch?.awayTeam}
				onClose={closePredictionModal}
				onCreditConsumed={handlePredictionUsed}
			/>

			<MatchOddsModal
				open={!!oddsModalMatch}
				matchId={oddsModalMatch?.id}
				homeTeam={oddsModalMatch?.homeTeam}
				awayTeam={oddsModalMatch?.awayTeam}
				onClose={closeOddsModal}
			/>
		</div>
	);
};

export default LeagueDetail;